import { FC } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  VESTING_GRAPH_AXIS_LINE_COLOR,
  VESTING_GRAPH_GRID_COLOR,
  VESTING_GRAPH_HEIGHT_PX,
  VESTING_GRAPH_ORIGINAL_SCHEDULE_COLOR,
  VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
  VESTING_GRAPH_TOOLTIP_CURSOR_COLOR,
  VESTING_GRAPH_UNVESTED_COLOR,
  VESTING_GRAPH_VESTED_COLOR,
  VESTING_GRAPH_Y_AXIS_WIDTH,
  VESTING_GRAPH_Y_DOMAIN_FLOOR,
} from '../model/vesting-graph.constants';
import { VestingGraphSeriesPoint } from '../model/vesting-graph.types';
import {
  resolveBarFill,
  VestingGraphBarShape,
  VestingGraphBarShapeProps,
} from './vesting-graph-bar';
import { VestingGraphMilestoneColumnCursor } from './vesting-graph-milestones';
import { VestingGraphTooltipSync } from './vesting-graph-tooltip-sync';
import { useVestingGraphContext } from './vesting-graph-view-context';

export const VestingGraphChart: FC = () => {
  const { vestingGraph, view } = useVestingGraphContext();
  const {
    barSize,
    chartData,
    chartMinWidth,
    chartPointer,
    chartShellRef,
    domainMax,
    domainMin,
    domainSpan,
    hasOriginalSchedule,
    hoveredPeriod,
    hoveredPoint,
    introAreaDurationMs,
    introDelayForBar,
    introPhase,
    isHoverLive,
    nextVestIndex,
    plotWidth,
    setChartPointer,
    setChartTooltip,
    variant,
  } = view;
  const tooltipCursor = !isHoverLive
    ? false
    : variant.showMilestoneMarkers
      ? <VestingGraphMilestoneColumnCursor />
      : {
          stroke: VESTING_GRAPH_TOOLTIP_CURSOR_COLOR,
          strokeDasharray: '4 4',
          strokeWidth: 1,
        };

  const statusAreaType = variant.showMilestoneMarkers ? 'linear' : 'stepAfter';
  const statusAreaConnectNulls = !variant.showMilestoneMarkers;
  const cliffEndTimestamp = vestingGraph.cliff
    ? new Date(vestingGraph.cliff.endDate).getTime()
    : null;

  return (
    <div className="relative z-[2]" ref={chartShellRef}>
      <ResponsiveContainer height={VESTING_GRAPH_HEIGHT_PX} minWidth={chartMinWidth} width="100%">
        <ComposedChart
          data={chartData}
          margin={{
            bottom: 8,
            left: 0,
            right: VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
            top: variant.topMargin,
          }}
          onMouseLeave={() => setChartPointer(null)}
          onMouseMove={
            variant.showMilestoneMarkers
              ? (state) => {
                  if (typeof state.chartX === 'number' && typeof state.chartY === 'number') {
                    setChartPointer({ x: state.chartX, y: state.chartY });
                  }
                }
              : undefined
          }
        >
          <CartesianGrid horizontal stroke={VESTING_GRAPH_GRID_COLOR} vertical={false} />
          <XAxis
            axisLine={{ stroke: VESTING_GRAPH_AXIS_LINE_COLOR }}
            dataKey="timestamp"
            domain={[domainMin, domainMax]}
            height={1}
            tick={false}
            tickLine={false}
            type="number"
          />
          <YAxis
            axisLine={{ stroke: VESTING_GRAPH_AXIS_LINE_COLOR }}
            domain={[VESTING_GRAPH_Y_DOMAIN_FLOOR, 100]}
            tick={{ fill: VESTING_GRAPH_AXIS_LINE_COLOR, fontSize: 10, fontWeight: 500 }}
            tickFormatter={(value) => (value === 0 ? '0' : `${value}%`)}
            tickLine={false}
            tickMargin={8}
            ticks={[0, 20, 40, 60, 80, 100]}
            width={VESTING_GRAPH_Y_AXIS_WIDTH}
          />
          {introPhase !== 'pending' && hasOriginalSchedule && (
            <Area
              activeDot={false}
              animationDuration={introAreaDurationMs}
              animationEasing="ease-out"
              connectNulls
              dataKey="originalSchedulePercent"
              fill={VESTING_GRAPH_ORIGINAL_SCHEDULE_COLOR}
              fillOpacity={variant.originalScheduleOpacity}
              isAnimationActive={introPhase === 'playing'}
              stroke={VESTING_GRAPH_ORIGINAL_SCHEDULE_COLOR}
              strokeDasharray="6 4"
              strokeWidth={2}
              type="stepAfter"
            />
          )}
          {introPhase !== 'pending' && variant.statusAreaOpacity && (
            <Area
              activeDot={false}
              animationDuration={introAreaDurationMs}
              animationEasing="ease-out"
              connectNulls={statusAreaConnectNulls}
              dataKey="vestedPercent"
              fill={VESTING_GRAPH_VESTED_COLOR}
              fillOpacity={variant.statusAreaOpacity.vested}
              isAnimationActive={introPhase === 'playing'}
              stroke="none"
              type={statusAreaType}
            />
          )}
          {introPhase !== 'pending' && variant.statusAreaOpacity && (
            <Area
              activeDot={false}
              animationDuration={introAreaDurationMs}
              animationEasing="ease-out"
              connectNulls={statusAreaConnectNulls}
              dataKey="projectedPercent"
              fill={VESTING_GRAPH_UNVESTED_COLOR}
              fillOpacity={variant.statusAreaOpacity.projected}
              isAnimationActive={introPhase === 'playing'}
              stroke="none"
              type={statusAreaType}
            />
          )}
          {variant.showValueBars && (
            <Bar
              barSize={barSize}
              dataKey="cumulativePercent"
              isAnimationActive={false}
              shape={(props: unknown) => {
                const barProps = props as Omit<
                  VestingGraphBarShapeProps,
                  'domainSpan' | 'introDelayMs' | 'introPhase' | 'nextVestIndex' | 'plotWidth'
                >;

                return (
                  <VestingGraphBarShape
                    {...barProps}
                    domainSpan={domainSpan}
                    introDelayMs={introDelayForBar(barProps.index ?? 0)}
                    introPhase={introPhase}
                    nextVestIndex={nextVestIndex}
                    plotWidth={plotWidth}
                  />
                );
              }}
            >
              {chartData.map((point, index) => {
                const isNextVest = index === nextVestIndex;
                const isEventOnlyColumn = Boolean(point.isEventColumn) && point.periodShares <= 0;
                const isInsideCliff =
                  Boolean(point.isCliffSpan) ||
                  (cliffEndTimestamp != null && point.calendarTimestamp < cliffEndTimestamp);

                return (
                  <Cell
                    fill={
                      index === 0 || isInsideCliff || (isEventOnlyColumn && !isNextVest)
                        ? 'transparent'
                        : resolveBarFill({ hoveredPeriod, hoveredPoint, point })
                    }
                    key={`${point.calendarTimestamp}-${index}`}
                  />
                );
              })}
            </Bar>
          )}
          <Tooltip
            active={isHoverLive ? undefined : false}
            content={(props) => (
              <VestingGraphTooltipSync
                active={props.active}
                chartShellRef={chartShellRef}
                coordinate={props.coordinate}
                enabled={isHoverLive}
                milestoneChartData={variant.showMilestoneMarkers ? chartData : undefined}
                onChange={setChartTooltip}
                payload={props.payload as Array<{ payload: VestingGraphSeriesPoint }> | undefined}
                pointer={variant.showMilestoneMarkers ? chartPointer : null}
              />
            )}
            cursor={tooltipCursor}
            wrapperStyle={{ outline: 'none', pointerEvents: 'none', visibility: 'hidden' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
